import { get, post } from 'api/client';
import { User, InviteUserParams, InviteUserResponse } from 'types/User';
import env from 'config';
import { UserPermission } from 'constants/userPermission';
import { jsonToFormData } from 'utils/formData';

type UserSession = {
  last_access: string;
};

type UserSessionsResponse = {
  sessions: UserSession[];
};

type RequestInvitationParams = {
  name: string;
  email: string;
  userEmail: string;
  accountUsername: string;
  token: string;
};

const UserApi = {
  getData(options?: { headers?: Record<string, string> }): Promise<User> {
    return get<User>('/me/data', {
      baseURL: env.API_SERVICE_ENDPOINT,
      ...(options?.headers && { headers: options.headers }),
    });
  },
  getPermissions(): Promise<UserPermission[]> {
    return get<UserPermission[]>('/me/permissions', {
      baseURL: env.API_SERVICE_ENDPOINT,
    });
  },
  requestInvitation(params: RequestInvitationParams): Promise<boolean> {
    return get('/user/requestInvitation', {
      baseURL: env.PUBLIC_API_SERVICE_ENDPOINT,
      params,
    });
  },

  sendInvitation(params: InviteUserParams): Promise<InviteUserResponse> {
    return post(
      '/users/invite',
      {
        email: params.email,
        role: params.role,
        first_name: params.first_name,
        last_name: params.last_name,
      },
      { baseURL: env.API_SERVICE_ENDPOINT },
    );
  },

  assignProfilesToUser(params: {
    userId: number;
    pageIds: string[];
  }): Promise<{ status: boolean }> {
    const formData = jsonToFormData({
      userId: params.userId.toString(),
      pageIds: JSON.stringify(params.pageIds),
      no_email: 1,
    });

    return post('/user/ajaxAddPagesToUser', formData);
  },

  assignProfileGroupsToUser(params: {
    userId: number;
    profileGroupIds: string[];
  }): Promise<{ status: boolean }> {
    const formData = jsonToFormData({
      user_id: params.userId.toString(),
      preset_ids: params.profileGroupIds.join(','),
    });

    return post('/presets/ajaxAddPresetsToUser', formData);
  },

  getSessions(): Promise<UserSessionsResponse> {
    return get('/user-account/sessions', {
      baseURL: env.API_SERVICE_ENDPOINT,
    });
  },

  removeProfileGroupFromUser(params: {
    userId: number;
    presetId: number;
  }): Promise<{ status: boolean }> {
    const formData = jsonToFormData({
      user_id: params.userId.toString(),
      preset_id: params.presetId.toString(),
    });

    return post('/presets/ajaxRemovePresetUser', formData);
  },

  removeProfileFromUser(params: {
    userId: number;
    pageId: number;
  }): Promise<{ status: boolean }> {
    const formData = jsonToFormData({
      userId: params.userId.toString(),
      pageId: params.pageId.toString(),
    });

    return post('/user/ajaxRemoveUserFromThePage', formData);
  },
};

export default UserApi;
